import { useState, useEffect, useCallback } from 'react';
import ListenButton from '@/components/atoms/ListenButton/ListenButton'
import RecordingBox from '@/components/atoms/RecordingBox/RecordingBox'
import * as styles from '@/components/organisms/Learning/LearningSytle.css'
import clsx from 'clsx'
import { getStudentActivityTitle } from '@/utils/activityTitle';

interface FillBlankProps {
  activity?: any;
  unitData?: any;
  onResponse?: (response: any) => void;
  existingResponse?: any;
  isHomework?: boolean;
  registerSubmitValidator?: (validator: (() => Promise<boolean> | boolean) | null) => void;
}

export default function StudentFillBlank({ activity, unitData, onResponse, existingResponse, isHomework = false, registerSubmitValidator }: FillBlankProps) {
    // activity.items에서 실제 데이터 가져오기
    const activityData = activity?.items?.[0] || {};
    let content: any = {};
    
    try {
        content = activityData.contentJson ? JSON.parse(activityData.contentJson) : {};
    } catch (e) {
        console.error('contentJson 파싱 에러:', e);
    }

    const sentence = content.sentence || content.text || "We also didn't know the names of flowers.";
    const translation = content.translation || content.korean || '나는 겨울을 좋아한다.';
    const blankWordsRaw = content.blankWords || content.blanks || "didn't know the names";
    const blankWordsBase = typeof blankWordsRaw === 'string' ? blankWordsRaw : '';
    const audioUrl = content.audioUrl || content.audio || null;
    const title = getStudentActivityTitle(activity?.title, '빈칸채우기');

    console.log('=== FillBlank 데이터 ===');
    console.log('Activity:', activity);
    console.log('ActivityData:', activityData);
    console.log('Content:', content);
    console.log('Sentence:', sentence);
    console.log('Translation:', translation);
    console.log('Blank Words:', blankWordsBase);
    console.log('Audio URL:', audioUrl);

    const getByteLength = (value: string) => {
        let len = 0;
        for (let i = 0; i < value.length; i++) {
            len += value.charCodeAt(i) > 127 ? 2 : 1;
        }
        return len;
    };

    const isHomeworkReady = (text: string) => getByteLength(text.trim()) >= 1;

    // 문장을 앞부분, 빈칸, 뒷부분으로 나누기
    const applyTrailingDotsToBlank = (sourceSentence: string, blank: string) => {
        if (!sourceSentence || !blank) return { blankText: blank, suffixTrim: '' };
        const idx = sourceSentence.indexOf(blank);
        if (idx < 0) return { blankText: blank, suffixTrim: '' };
        const after = sourceSentence.slice(idx + blank.length);
        const dotMatch = after.match(/^\.+/);
        if (!dotMatch || blank.endsWith('.')) return { blankText: blank, suffixTrim: '' };
        return { blankText: `${blank}${dotMatch[0]}`, suffixTrim: dotMatch[0] };
    };

    const { blankText, suffixTrim } = applyTrailingDotsToBlank(sentence, blankWordsBase);
    const blankStartIndex = sentence.indexOf(blankWordsBase);
    const beforeBlank = blankStartIndex > 0 ? sentence.substring(0, blankStartIndex) : '';
    let afterBlank = blankStartIndex + blankWordsBase.length < sentence.length 
        ? sentence.substring(blankStartIndex + blankWordsBase.length) 
        : '';
    if (suffixTrim && afterBlank.startsWith(suffixTrim)) {
        afterBlank = afterBlank.substring(suffixTrim.length);
    }

    console.log('Before Blank:', beforeBlank);
    console.log('Blank Words:', blankText);
    console.log('After Blank:', afterBlank);

    const correctAnswer = blankText;
    const correctChars = correctAnswer.split('');
    const [inputText, setInputText] = useState(existingResponse?.inputText || '');
    const [wrongCount, setWrongCount] = useState(existingResponse?.wrongCount || 0);


  const inputChars = inputText.split('');
  const hasInput = inputChars.length > 0;
  const isPrefixMatch = inputChars.every((char: string, idx: number) => char === correctChars[idx]);
  const isFullyCorrect = isPrefixMatch && inputChars.length === correctChars.length;
  const isTypingIncorrect = hasInput && !isPrefixMatch;
    
    const emitResponse = useCallback((value: string, extra: Record<string, any> = {}) => {
        if (!onResponse) return;
        const valueChars = value.split('');
        const valueMatches = valueChars.every((char: string, idx: number) => char === correctChars[idx]);
        onResponse({
            inputText: value,
            isCorrect: valueMatches && valueChars.length === correctChars.length,
            isHomeworkReady: isHomeworkReady(value),
            wrongCount,
            ...extra
        });
    }, [correctChars, isHomeworkReady, onResponse, wrongCount]);

    const handleWrongAnswer = useCallback((value?: string) => {
        if (isHomework) {
            emitResponse(value ?? inputText, { isCorrect: false });
            return;
        }
        const nextWrong = wrongCount + 1;
        setWrongCount(nextWrong);
        emitResponse(value ?? inputText, { isCorrect: false, wrongCount: nextWrong });
    }, [emitResponse, inputText, isHomework, wrongCount]);

    // 마운트 시 초기 응답 저장
    useEffect(() => {
        if (onResponse && !existingResponse) {
            emitResponse('', { isCorrect: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    // inputText 변경 시 응답 저장 (실시간 정답 여부는 유지, 오답 카운트는 하지 않음)
    const handleInputChange = (value: string) => {
        setInputText(value);
        emitResponse(value, { isCorrect: value === correctAnswer });
    };

    useEffect(() => {
        if (!registerSubmitValidator) return;

        const validator = () => {
            const trimmed = inputText.trim();
            const hasMinLength = isHomeworkReady(trimmed);
            const isExactMatch = inputText === correctAnswer;

            if (isExactMatch) {
                emitResponse(inputText, { isCorrect: true });
                return true;
            }

            if (!hasMinLength) {
                emitResponse(inputText, { isCorrect: false });
                return false;
            }

            handleWrongAnswer(inputText);
            return false;
        };

        registerSubmitValidator(() => validator());
        return () => registerSubmitValidator(null);
    }, [registerSubmitValidator, inputText, correctAnswer, emitResponse, handleWrongAnswer, isHomeworkReady]);
  return (
    <main>
     <div>
        <h1 className='px-5 pt-5 pb-3 heading1'>{title}</h1>
        <div className='p-5 pt-0'>
          <div className={styles.gameCard}>
            <div className='py-4 px-14'>
                <div className='relative'>
                    <p className='!text-2xl font-bold pb-2 text-center break-words'>{sentence}</p>
                </div>
                {translation && <p className='text-center text-gray-500 mt-2'>{translation}</p>}
            </div>
                {audioUrl && <ListenButton size={48} className='absolute right-4 top-4' audioSrc={audioUrl} />}
          </div>
            <section className='flex items-center justify-center p-5 border border-gray-100 bg-gray-50 rounded-2xl'>
                
                <p className="inline-flex flex-wrap items-center text-2xl font-bold">
                            {beforeBlank && <span className="h-[36px] mr-2">{beforeBlank}</span>}
                            <div
                              className={clsx(
                                'relative inline-flex rounded-lg px-1 py-0.5 transition-all duration-200',
                                {
                                  'bg-emerald-100 ring-2 ring-emerald-300': isFullyCorrect,
                                  'bg-green-50 ring-2 ring-green-200': hasInput && isPrefixMatch && !isFullyCorrect,
                                  'bg-red-50 ring-2 ring-red-200': isTypingIncorrect,
                                }
                              )}
                            >
                                {correctChars.map((char: string, idx: number) => {
                                    const inputChar = inputChars[idx];
                                    const isMatch = inputChar === char;
                                    return (
                                    <span
                                        key={idx}
                                        className={clsx(
                                          'relative min-w-[0.5rem] border-black border-b-2 text-center h-[36px] transition-colors duration-150',
                                          {
                                            'text-emerald-600': Boolean(inputChar) && isFullyCorrect && isMatch,
                                            'text-green-500': Boolean(inputChar) && !isFullyCorrect && isMatch,
                                            'text-red-500': Boolean(inputChar) && !isMatch,
                                          }
                                        )}
                                    >
                                        {inputChar && (
                                        <span
                                            className="h-[36px]"
                                        >
                                            {inputChar}
                                        </span>
                                        )}
                                        {/* 빈칸일 때도 높이 확보 */}
                                        {!inputChar && <span className="invisible">_</span>}
                                    </span>
                                    );
                                })}
                            </div>                            
                            {afterBlank && <span className="h-[36px] ml-2">{afterBlank}</span>}
                        </p>
            </section>
            <section className='mt-5'>
                <p className='mb-3 text-sm text-center text-gray-500'>
                    빈칸에 들어갈 단어를 입력해주세요.
                </p>
                <input
                value={inputText}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder="띄어쓰기 포함해서 입력해보세요"
                type="text"
                className={clsx(
                  styles.graInput,
                  'transition-all duration-200',
                  {
                    'ring-2 ring-emerald-300 focus:ring-emerald-400': isFullyCorrect,
                    'ring-2 ring-green-200 focus:ring-green-300': hasInput && isPrefixMatch && !isFullyCorrect,
                    'ring-2 ring-red-200 focus:ring-red-300': isTypingIncorrect,
                  }
                )}
                style={{
                  color: hasInput ? (isPrefixMatch ? '#059669' : '#dc2626') : undefined,
                  caretColor: hasInput ? (isPrefixMatch ? '#059669' : '#dc2626') : undefined,
                }}
                />
            </section>
                                                               
        </div>
     </div>
    </main>
  )
}